/**
 * retryScheduler.ts
 *
 * Manages exponential backoff retry timers for failed sync operations.
 * The SyncWatcher calls scheduleRetry() when an operation fails.
 * After the delay, it calls wake() on the SyncWatcher to retry.
 */

const BASE_DELAY_MS = 1000;       // 1 second base
const MAX_DELAY_MS = 30_000;      // 30 seconds cap
const JITTER_MS = 500;            // random jitter to prevent thundering herd

/**
 * Calculate the backoff delay for a given attempt number.
 *
 * Formula: min(baseDelay * 2^attempts, maxDelay) + random(0, jitter)
 *
 * Attempt 1 → ~1s
 * Attempt 2 → ~2s
 * Attempt 3 → ~4s
 * Attempt 4 → ~8s
 * Attempt 5 → ~16s
 * Attempt 6+ → ~30s (capped)
 */
export function calculateBackoffDelay(attempts: number): number {
  const exponential = BASE_DELAY_MS * Math.pow(2, attempts - 1);
  const capped = Math.min(exponential, MAX_DELAY_MS);
  const jitter = Math.random() * JITTER_MS;
  return Math.floor(capped + jitter);
}

class RetryScheduler {
  private timers: Map<string, ReturnType<typeof setTimeout>> = new Map();

  /**
   * Schedule a retry for a failed operation after the appropriate backoff delay.
   *
   * @param opId        The syncQueue operation ID
   * @param attempts    How many times this op has already been attempted
   * @param onRetry     Callback to invoke when the timer fires (usually SyncWatcher.wake)
   */
  schedule(opId: string, attempts: number, onRetry: () => void): void {
    // Cancel any existing timer for this op before scheduling a new one
    this.cancel(opId);

    const delay = calculateBackoffDelay(attempts);
    const timer = setTimeout(() => {
      this.timers.delete(opId);
      onRetry();
    }, delay);

    this.timers.set(opId, timer);
  }

  /** Cancel a scheduled retry for a specific operation. */
  cancel(opId: string): void {
    const existing = this.timers.get(opId);
    if (existing) {
      clearTimeout(existing);
      this.timers.delete(opId);
    }
  }

  /** Cancel all scheduled retries. Called when sync succeeds or app unmounts. */
  cancelAll(): void {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
  }

  /** Returns how many retries are currently scheduled. */
  get pendingCount(): number {
    return this.timers.size;
  }
}

// Singleton — one scheduler per app
export const retryScheduler = new RetryScheduler();
